// File for logic corresponding to article component
import { Component } from 'js-component-framework';
import Debounce from 'utils/Debounce';
import { getSessionVal } from 'utils/session';
import CaptionToggle from './captionToggle';
import './index.scss';

/**
 * Component for a single article.
 */
export default class Article extends Component {
  /**
   * Set caption clamping on article blocks.
   *
   * @memberof Article
   * @param HTMLElement blockElement block wrapping element.
   * @param String captionsSelector selector of the caption(s) to apply clamp.
   */
  static setCaptionClamp(blockElement, captionsSelector) {
    if (! blockElement || ! (blockElement instanceof NodeList)) {
      return;
    }
    Array.prototype.forEach.call(blockElement, (wrappingEl) => {
      const captions = wrappingEl.querySelectorAll(captionsSelector);

      if (! captions || ! (captions instanceof NodeList)) {
        return;
      }

      Array.prototype.forEach.call(captions, (el) => {
        const caption = new CaptionToggle(el, 1);
        caption.init();
      });
    });
  }

  /**
   * Start the component
   */
  constructor(config) {
    super(config);

    this.readMoreButton = null;
    this.dismissed = false;
    this.nxstory = false;
    this.isExpanded = false;
    const { slideshowBlock, galleryBlock } = this.children;
    const { slideshowCaptionClass, galleryCaptionClass } = this.options;

    this.shouldArticleCondense();
    this.setInlineRelatedStoriesPos();

    Article.setCaptionClamp(slideshowBlock, slideshowCaptionClass);
    Article.setCaptionClamp(galleryBlock, galleryCaptionClass);

    this.onResize = this.onResize.bind(this);
    this.onResizeDebounce = new Debounce(
      this.onResize,
      'resize',
      window,
      250,
      false,
    );

    // Add event listener for 'continue reading' button.
    this.element.addEventListener('click', (e) => {
      if (e.target.classList.contains(this.options.readMoreButtonClass)) {
        this.setArticleExpanded();
      }
    });

    // Check if read more button is enabled in mobile
    if ('undefined' !== typeof window.isMobileReadMoreEnabled
    && ! window.isMobileReadMoreEnabled) {
      this.setArticleExpanded();
    }

    // Display article lists if FB referral
    const facebookRefList = document.querySelectorAll('.article-list-fb-only');
    if ('fb' === getSessionVal('ns.gpt.refGroup')) {
      facebookRefList.forEach((articleList) => {
        articleList.classList.remove('article-list-fb-only');
      });
    }

    // nxstory mvp
    this.loadNXStory();
  }

  /**
   * Do stuff upon viewport resize.
   */
  onResize() {
    this.shouldArticleCondense();
    this.setInlineRelatedStoriesPos();
  }

  /**
   * Set article to condensed state.
   */
  setArticleCondensed() {
    this.element.classList.add(this.options.articleCondensedClass);
    this.createButton();
  }

  /**
   * Set article to expanded state.
   */
  setArticleExpanded() {
    this.element.classList.remove(this.options.articleCondensedClass);

    // Set true even if the article expands just by virtue of the screen width
    // increasing so that someone isn't ejected from the article if the width
    // decreases again. Think a rotating tablet.
    this.dismissed = true;

    this.destroyButton();

    this.isExpanded = true;

    // Show hidden ads in article body once content expands.
    window.dispatchEvent(new CustomEvent('nsDfpShowHidden'));
  }

  /**
   * Set inline related stories position
   */
  setInlineRelatedStoriesPos() {
    const relatedStories = this.children.inlineRelatedStories;

    if (! relatedStories) {
      return;
    }

    const grafs = [...this.children.content.children]
      .filter((child) => child.matches('p'));

    // Repositioning only needs to happen if there is a single graf.
    if (1 !== grafs.length) {
      return;
    }

    if (matchMedia('(min-width: 768px)').matches) {
      this.children.content.insertAdjacentElement(
        'afterbegin',
        relatedStories,
      );
    } else if (matchMedia('(max-width: 768px)').matches) {
      this.children.content.insertAdjacentElement(
        'beforeend',
        relatedStories,
      );
    }
  }

  /**
   * Determine if article content should be in condensed or expanded format.
   */
  shouldArticleCondense() {
    if (this.dismissed) {
      return;
    }

    const { continue_reading_cta: feature } = window.nexstar;

    const contentHeight = this.children.content.offsetHeight;
    const shouldCondense = feature.min_content_height <= contentHeight
      && matchMedia(`(max-width: ${feature.breakpoint}px)`).matches;

    if (shouldCondense) {
      this.setArticleCondensed();
    } else {
      this.setArticleExpanded();
    }
  }

  /**
   * Create 'continue reading' button.
   */
  createButton() {
    if (this.readMoreButton) {
      return;
    }

    const { continue_reading_cta: feature } = window.nexstar;

    this.readMoreButton = document.createElement('button');
    this.readMoreButton.classList.add(this.options.readMoreButtonClass);
    this.readMoreButton.innerText = feature.l10n.cta_text;
    this.children.content.appendChild(this.readMoreButton);
    dispatchEvent(new Event('continue-reading-button-created'));
  }

  /**
   * Destroy 'continue reading' button
   */
  destroyButton() {
    if (! this.readMoreButton) {
      return;
    }

    this.children.content.removeChild(this.readMoreButton);
    this.readMoreButton = null;
  }

  /**
   * Load NXStory card
   */
  loadNXStory() {
    $(window).scroll(() => {
      let targetBlock = $('.civic-science-container').first();
      let extraOffset = 350;

      if (0 === targetBlock.length) {
        targetBlock = $('#taboola-block');
        extraOffset = 0;
      }

      if (0 === targetBlock.length) {
        return;
      }

      const hT = targetBlock.offset().top;
      const hH = targetBlock.height();
      const hB = hT + hH + extraOffset;
      const wH = $(window).height();
      const wS = $(window).scrollTop();
      const isMobile = () => window.matchMedia('(max-width: 512px)').matches;

      if (wS > (hB - wH)) {
        if (! this.nxstory && this.isExpanded && isMobile()) {
          if ('function' === typeof initCounter) {
            // eslint-disable-next-line no-undef
            initCounter();
            this.nxstory = true;
          }
        }
      }
    });
  }
}
